/* global BigInt */
// src/components/SolanaWalletButton.js
import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { NETWORK_TOKENS } from '../utils/networkTokens';
import debugLog from '../utils/debugLog';
import Typography from '@mui/material/Typography';
import { Buffer } from 'buffer';
import normalizeWalletError from '../utils/normalizeWalletError';
import { toEpochMs } from '../utils/time';

// Add Solana Web3 imports
import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  clusterApiUrl
} from '@solana/web3.js';

// Add SPL Token imports
import {
  createTransferCheckedInstruction,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction
} from '@solana/spl-token';

// If your bundler doesn't automatically polyfill Buffer, add this line:
window.Buffer = Buffer;

const WalletButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#9945FF',
  color: 'white',
  '&:hover': {
    backgroundColor: '#8134E8',
  },
  marginTop: theme.spacing(2),
  width: '100%'
}));

const SOLANA_RPC_TIMEOUT_MS = 5000;
const SOLANA_PUBLIC_RPC_ENDPOINTS = {
  mainnet: [
    'https://api.mainnet.solana.com',
    'https://api.mainnet-beta.solana.com',
    'https://solana-api.projectserum.com',
    'https://rpc.ankr.com/solana'
  ],
  devnet: [
    clusterApiUrl('devnet'),
    'https://rpc.ankr.com/solana_devnet'
  ]
};

const parseRpcList = (value) => {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const buildSolanaRpcCandidates = (networkEnvironment) => {
  const isDevnet = networkEnvironment === 'devnet';
  const listOverride = isDevnet
    ? (process.env.REACT_APP_SOLANA_DEVNET_RPC_URLS || process.env.REACT_APP_SOLANA_RPC_URLS_DEVNET)
    : (process.env.REACT_APP_SOLANA_MAINNET_RPC_URLS || process.env.REACT_APP_SOLANA_RPC_URLS);
  const singleOverride = isDevnet
    ? (process.env.REACT_APP_SOLANA_DEVNET_RPC_URL || process.env.REACT_APP_SOLANA_RPC_URL_DEVNET)
    : (process.env.REACT_APP_SOLANA_MAINNET_RPC_URL || process.env.REACT_APP_SOLANA_RPC_URL);

  const candidates = [
    ...parseRpcList(listOverride),
    ...(singleOverride ? [singleOverride] : []),
    ...(isDevnet ? SOLANA_PUBLIC_RPC_ENDPOINTS.devnet : SOLANA_PUBLIC_RPC_ENDPOINTS.mainnet)
  ];

  const unique = [];
  const seen = new Set();
  for (const endpoint of candidates) {
    const trimmed = String(endpoint || '').trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    unique.push(trimmed);
  }
  return unique;
};

const withTimeout = (promise, timeoutMs, errorMessage) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(errorMessage || 'RPC timeout')), timeoutMs);
  promise
    .then((result) => {
      clearTimeout(timer);
      resolve(result);
    })
    .catch((error) => {
      clearTimeout(timer);
      reject(error);
    });
});

const getSolanaConnectionWithFallback = async (networkEnvironment, { timeoutMs = SOLANA_RPC_TIMEOUT_MS, probe } = {}) => {
  const endpoints = buildSolanaRpcCandidates(networkEnvironment);
  let lastError = null;

  for (const endpoint of endpoints) {
    try {
      const connection = new Connection(endpoint, 'confirmed');
      const result = probe
        ? await withTimeout(Promise.resolve(probe(connection)), timeoutMs, 'RPC timeout')
        : null;
      return { connection, endpoint, result };
    } catch (error) {
      lastError = error;
      debugLog('Solana RPC failed', {
        endpoint,
        message: error?.message || String(error)
      });
    }
  }

  console.error('All Solana RPC endpoints failed', lastError);
  throw new Error('Solana RPC is temporarily unavailable. Please try again.');
};

const confirmTransactionWithFallback = async ({
  connection,
  endpoint,
  networkEnvironment,
  signature,
  blockhash,
  lastValidBlockHeight
}) => {
  const confirmArgs = blockhash && lastValidBlockHeight
    ? [{ signature, blockhash, lastValidBlockHeight }, 'confirmed']
    : [signature, 'confirmed'];

  try {
    return await connection.confirmTransaction(...confirmArgs);
  } catch (error) {
    debugLog('Primary confirmTransaction failed', {
      endpoint,
      message: error?.message || String(error)
    });
  }

  const endpoints = buildSolanaRpcCandidates(networkEnvironment).filter((rpc) => rpc !== endpoint);
  let lastError = null;

  for (const rpc of endpoints) {
    try {
      const fallbackConnection = new Connection(rpc, 'confirmed');
      return await fallbackConnection.confirmTransaction(...confirmArgs);
    } catch (error) {
      lastError = error;
      debugLog('Fallback confirmTransaction failed', {
        endpoint: rpc,
        message: error?.message || String(error)
      });
    }
  }

  throw lastError || new Error('Transaction confirmation failed');
};

const fetchBlockhashFromBackend = async ({ sessionToken, sessionSignature }) => {
  if (!sessionToken || !sessionSignature) {
    throw new Error('Missing payment session credentials');
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/v1/iframe/solana/blockhash`,
    {
      method: 'GET',
      headers: {
        'X-Session-Token': sessionToken,
        'X-Signature': sessionSignature
      }
    }
  );

  if (!response.ok) {
    let message = 'Failed to fetch Solana blockhash';
    try {
      const data = await response.json();
      if (data?.error) {
        message = data.error;
      }
    } catch (_e) {}
    throw new Error(message);
  }

  const data = await response.json();
  if (!data?.blockhash || !Number.isFinite(Number(data?.last_valid_block_height))) {
    throw new Error('Invalid blockhash response');
  }

  return {
    blockhash: data.blockhash,
    lastValidBlockHeight: Number(data.last_valid_block_height)
  };
};

const fetchSplTransferInfoFromBackend = async ({
  sessionToken,
  sessionSignature,
  mintAddress,
  senderAddress,
  recipientAddress
}) => {
  if (!sessionToken || !sessionSignature) {
    throw new Error('Missing payment session credentials');
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/v1/iframe/solana/token-transfer-info`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': sessionToken,
        'X-Signature': sessionSignature
      },
      body: JSON.stringify({
        mint_address: mintAddress,
        sender_address: senderAddress,
        recipient_address: recipientAddress
      })
    }
  );

  if (!response.ok) {
    let message = 'Failed to fetch Solana token transfer info';
    try {
      const data = await response.json();
      if (data?.error) {
        message = data.error;
      }
    } catch (_e) {}
    throw new Error(message);
  }

  const data = await response.json();
  if (!data?.program_id || !data?.recipient_token_account) {
    throw new Error('Invalid token transfer info response');
  }

  return data;
};

const SolanaWalletButton = ({ 
  tokenSymbol, 
  amount,
  paymentAddress,
  networkEnvironment,
  sessionToken,
  sessionSignature,
  tokenDecimals,
  tokenMintAddress,
  onSuccess,
  onError,
  onTransactionSubmitted,
  onTransactionConfirmed,
  onTransactionFailed,
  expiresAt,
  paymentDisabled,
  onPaymentInitiated
}) => {
  const [sending, setSending] = useState(false);
  const [phantom, setPhantom] = useState(null);
  
  // Calculate if the payment is about to expire
  const isExpiringSoon = useMemo(() => {
    if (!expiresAt) return false;
    
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    const timeRemainingMs = expirationTime - now;
    
    // Return true if less than 1 minute (60,000 ms) remaining
    return timeRemainingMs < 60000 && timeRemainingMs > 0;
  }, [expiresAt]);
  
  // Check if already expired
  const isExpired = useMemo(() => {
    if (!expiresAt) return false;
    
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    return now >= expirationTime;
  }, [expiresAt]);
  
  // Helper function to parse error messages into user-friendly format
  const getReadableError = (error) => {
    const errorMessage = error.message || String(error);

    if (errorMessage.includes('Failed to fetch Solana blockhash')) {
      return 'Solana RPC is temporarily unavailable. Please try again.';
    }

    if (errorMessage.includes('Failed to fetch Solana token transfer info')) {
      return 'Solana RPC is temporarily unavailable. Please try again.';
    }

    if (errorMessage.includes('Solana RPC is temporarily unavailable')) {
      return 'Solana RPC is temporarily unavailable. Please try again.';
    }

    if (errorMessage.includes("Invalid amount format")) {
      return "Invalid amount format. Please refresh to get a new quote.";
    }

    if (errorMessage.includes("decimal places")) {
      return "Amount has too many decimal places for this token.";
    }
    
    return normalizeWalletError(error, "Transaction failed. Please try again.", {
      network: 'solana',
      wallet: 'phantom'
    });
  };

  // Convert decimal string amounts to base units without floating point loss.
  const parseTokenAmountToBaseUnits = (value, decimals) => {
    if (!Number.isInteger(decimals) || decimals < 0) {
      throw new Error("Invalid token decimals");
    }

    const normalized = String(value ?? '').trim();
    if (!/^\d+(\.\d+)?$/.test(normalized)) {
      throw new Error("Invalid amount format");
    }

    const [whole, fraction = ''] = normalized.split('.');
    const trimmedFraction = fraction.slice(0, decimals);
    const paddedFraction = trimmedFraction.padEnd(decimals, '0');
    const digits = `${whole}${paddedFraction}`.replace(/^0+/, '') || '0';
    let baseUnits = BigInt(digits);

    if (fraction.length > decimals && /[1-9]/.test(fraction.slice(decimals))) {
      baseUnits += 1n;
    }

    return baseUnits;
  };

  const formatBaseUnitsToDecimalString = (value, decimals) => {
    if (!Number.isInteger(decimals) || decimals < 0) return String(value ?? '');
    const raw = value?.toString ? value.toString() : String(value ?? '0');
    if (!/^\d+$/.test(raw)) return raw;
    if (decimals === 0) return raw;
    const padded = raw.padStart(decimals + 1, '0');
    const whole = padded.slice(0, -decimals);
    const fraction = padded.slice(-decimals).replace(/0+$/, '');
    return fraction ? `${whole}.${fraction}` : whole;
  };

  const formatTokenAmountForDisplay = (value, decimals) => {
    if (value === null || value === undefined) return '0';

    const raw = String(value).trim();
    if (!raw) return '0';

    const match = raw.match(/^(\d+)(?:\.(\d+))?$/);
    if (!match) {
      const num = Number(raw);
      if (!Number.isFinite(num)) return raw;
      const fallbackDecimals = Number.isInteger(decimals) && decimals >= 0 ? decimals : 9;
      return num
        .toFixed(fallbackDecimals)
        .replace(/\.?0+$/, '');
    }

    let whole = match[1] || '0';
    let fraction = match[2] || '';
    if (Number.isInteger(decimals) && decimals >= 0) {
      fraction = fraction.slice(0, decimals);
    }

    fraction = fraction.replace(/0+$/, '');
    whole = whole.replace(/^0+(?=\d)/, '');

    return fraction ? `${whole}.${fraction}` : whole;
  };

  const resolveTokenProgramId = async (connection, mintPublicKey) => {
    const mintAccountInfo = await connection.getAccountInfo(mintPublicKey);
    if (!mintAccountInfo?.owner) {
      throw new Error("Token mint not found on network");
    }
    if (mintAccountInfo.owner.equals(TOKEN_PROGRAM_ID)) {
      return TOKEN_PROGRAM_ID;
    }
    if (mintAccountInfo.owner.equals(TOKEN_2022_PROGRAM_ID)) {
      return TOKEN_2022_PROGRAM_ID;
    }
    throw new Error("Unsupported token program for this mint");
  };

  const resolveSplTransferContext = async ({
    connection,
    sessionToken,
    sessionSignature,
    mintPublicKey,
    senderPublicKey,
    recipientPublicKey
  }) => {
    if (connection) {
      try {
        const tokenProgramId = await resolveTokenProgramId(connection, mintPublicKey);
        const recipientTokenAccount = await getAssociatedTokenAddress(
          mintPublicKey,
          recipientPublicKey,
          false,
          tokenProgramId,
          ASSOCIATED_TOKEN_PROGRAM_ID
        );
        const recipientTokenAccountInfo = await connection.getAccountInfo(recipientTokenAccount);

        let senderTokenAccount = await getAssociatedTokenAddress(
          mintPublicKey,
          senderPublicKey,
          false,
          tokenProgramId,
          ASSOCIATED_TOKEN_PROGRAM_ID
        );
        let senderTokenAccountInfo = await connection.getAccountInfo(senderTokenAccount);

        if (!senderTokenAccountInfo) {
          const fallbackAccounts = await connection.getParsedTokenAccountsByOwner(
            senderPublicKey,
            { programId: tokenProgramId }
          );
          const matchingAccount = fallbackAccounts.value.find((entry) => {
            const mint = entry?.account?.data?.parsed?.info?.mint;
            return mint === mintPublicKey.toBase58();
          });
          if (matchingAccount?.pubkey) {
            senderTokenAccount = matchingAccount.pubkey;
            senderTokenAccountInfo = { exists: true };
          }
        }

        let senderTokenBalance = null;
        if (senderTokenAccountInfo && senderTokenAccount) {
          try {
            const balanceResp = await connection.getTokenAccountBalance(senderTokenAccount);
            senderTokenBalance = balanceResp?.value?.amount ?? null;
          } catch (_e) {}
        }

        return {
          tokenProgramId,
          recipientTokenAccount,
          recipientTokenAccountExists: Boolean(recipientTokenAccountInfo),
          senderTokenAccount,
          senderTokenAccountExists: Boolean(senderTokenAccountInfo),
          senderTokenBalance
        };
      } catch (error) {
        debugLog('Public RPC token lookup failed, falling back to backend', {
          error: error?.message || String(error)
        });
      }
    }

    const backendInfo = await fetchSplTransferInfoFromBackend({
      sessionToken,
      sessionSignature,
      mintAddress: mintPublicKey.toBase58(),
      senderAddress: senderPublicKey.toBase58(),
      recipientAddress: recipientPublicKey.toBase58()
    });

    return {
      tokenProgramId: new PublicKey(backendInfo.program_id),
      recipientTokenAccount: new PublicKey(backendInfo.recipient_token_account),
      recipientTokenAccountExists: backendInfo.recipient_token_account_exists === true,
      senderTokenAccount: backendInfo.sender_token_account
        ? new PublicKey(backendInfo.sender_token_account)
        : null,
      senderTokenAccountExists: backendInfo.sender_token_account_exists === true,
      senderTokenBalance: backendInfo.sender_token_balance ?? null
    };
  };
  
  // Check if Phantom is available
  useEffect(() => {
    if (window.solana?.isPhantom) {
      setPhantom(window.solana);
    }
  }, []);

  const displayDecimals = useMemo(() => {
    const tokenDecimalsValue = Number.parseInt(tokenDecimals, 10);
    if (Number.isInteger(tokenDecimalsValue)) {
      return tokenDecimalsValue;
    }
    const network = networkEnvironment === 'devnet' ? 'devnet' : 'mainnet';
    return NETWORK_TOKENS.solana[network]?.[tokenSymbol]?.decimals ?? null;
  }, [networkEnvironment, tokenDecimals, tokenSymbol]);

  const displayAmount = useMemo(() => {
    if (!Number.isInteger(displayDecimals)) {
      return formatTokenAmountForDisplay(amount, displayDecimals);
    }
    try {
      const baseUnits = parseTokenAmountToBaseUnits(amount, displayDecimals);
      return formatBaseUnitsToDecimalString(baseUnits, displayDecimals);
    } catch (_e) {
      return formatTokenAmountForDisplay(amount, displayDecimals);
    }
  }, [amount, displayDecimals]);
  
  // Add this helper function near the top of your component
  const checkWalletNetwork = async (phantom, expectedNetwork) => {
    try {
      // First check if we can detect network directly
      if (phantom.networkVersion) {
        const currentNetwork = phantom.networkVersion;
        debugLog(`Wallet reports network: ${currentNetwork}`);
        
        if (expectedNetwork === 'devnet' && currentNetwork !== 'devnet') {
          return false;
        }
      }
      
      // Check balance as a test to see if the wallet can interact with this network
      const publicKey = phantom.publicKey;
      if (publicKey) {
        try {
          await getSolanaConnectionWithFallback(expectedNetwork, {
            probe: (connection) => connection.getBalance(publicKey)
          });
          debugLog(`Successfully connected to ${expectedNetwork}`);
          return true;
        } catch (e) {
          console.error(`Failed to connect to ${expectedNetwork}:`, e);
          return false;
        }
      }
      
      return true; // Default to true if we can't determine
    } catch (e) {
      console.error("Error checking wallet network:", e);
      return true; // Default to true if check fails
    }
  };
  
  // Modified handlePayment function to enforce network selection
  const handlePayment = async () => {
    let signature;
    try {
      setSending(true);
      
      debugLog("=== PAYMENT FLOW STARTED ===");
      debugLog("Network environment:", networkEnvironment);
      debugLog("Token symbol:", tokenSymbol);
      
      // Check if phantom exists and log its state
      if (!phantom) {
        console.error("Phantom extension not found");
        throw new Error("Phantom wallet extension not found");
      }
      
      debugLog("Phantom detected:", {
        isPhantom: phantom.isPhantom,
        hasPublicKey: !!phantom.publicKey,
        publicKey: phantom.publicKey?.toString() || 'none',
        isConnected: !!phantom.isConnected,
        connectionStatus: phantom.isConnected ? 'connected' : 'disconnected'
      });
      
      // IMPORTANT: Verify and enforce network selection
      if (networkEnvironment === 'devnet') {
        debugLog("ENFORCING DEVNET TRANSACTION");
        
        // Force user to confirm they are on devnet
        const proceedWithDevnet = window.confirm(
          "⚠️ IMPORTANT: This is a DEVNET transaction.\n\n" +
          "Before proceeding:\n" +
          "1. Make sure your Phantom wallet is set to DEVNET\n" +
          "2. Settings > Developer Settings > Change Network > Devnet\n\n" +
          "Are you sure your wallet is configured for Devnet?"
        );
        
        if (!proceedWithDevnet) {
          throw new Error("Devnet transaction canceled - please configure wallet for devnet");
        }
      }

      if (onPaymentInitiated) {
        onPaymentInitiated();
      }
      
      // Get token info based on network environment
      const network = networkEnvironment === 'devnet' ? 'devnet' : 'mainnet';
      const tokenInfo = NETWORK_TOKENS.solana[network]?.[tokenSymbol] || {};
      const tokenDecimalsValue = Number.parseInt(tokenDecimals, 10);
      const resolvedTokenDecimals = Number.isInteger(tokenDecimalsValue)
        ? tokenDecimalsValue
        : tokenInfo.decimals;
      const resolvedMintAddress = tokenMintAddress || tokenInfo.mintAddress;

      if (!Number.isInteger(resolvedTokenDecimals)) {
        console.error("Token decimals not found:", {
          network,
          tokenSymbol,
          tokenDecimals,
          tokenInfo,
          availableTokens: Object.keys(NETWORK_TOKENS.solana[network] || {})
        });
        throw new Error(`Token decimals not found for ${tokenSymbol}`);
      }

      if (tokenSymbol !== 'SOL' && !resolvedMintAddress) {
        console.error("Token mint not found:", {
          network,
          tokenSymbol,
          tokenMintAddress,
          tokenInfo,
          availableTokens: Object.keys(NETWORK_TOKENS.solana[network] || {})
        });
        throw new Error(`Mint address not found for ${tokenSymbol}`);
      }
      
      debugLog("Token info:", {
        ...tokenInfo,
        decimals: resolvedTokenDecimals,
        mintAddress: resolvedMintAddress
      });
      
      // DEVNET WARNING
      if (networkEnvironment === 'devnet') {
        debugLog("DEVNET transaction detected");
        
        const proceedWithDevnet = window.confirm(
          "This is a DEVNET transaction. Please ensure your Phantom wallet is:\n\n" +
          "1. Set to Devnet network in Settings > Developer Settings\n" +
          "2. Has sufficient devnet SOL (you can get from a faucet)\n\n" +
          "Continue with connection?"
        );
        
        if (!proceedWithDevnet) {
          throw new Error("Devnet transaction canceled by user");
        }
      }
      
      // Try skipping disconnect if public key is already available
      if (phantom.publicKey) {
        debugLog("Public key already available:", phantom.publicKey.toString());
        debugLog("Skipping disconnect/connect cycle");
      } else {
        // Reset any previous connections
        try {
          debugLog("Attempting to disconnect existing connection");
          if (phantom.disconnect) {
            await phantom.disconnect();
            debugLog("Successfully disconnected");
            
            // Give a moment for the wallet to reset
            debugLog("Waiting for wallet state to reset...");
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (e) {
          debugLog("No active connection to disconnect or error:", e);
        }
        
        // Try explicit connection with detailed error logging
        debugLog("Attempting to connect to wallet...");
        try {
          // Log wallet state before connection
          debugLog("Wallet state before connection:", {
            hasPublicKey: !!phantom.publicKey,
            isConnected: !!phantom.isConnected
          });
          
          // Attempt connection with more options
          const connectOptions = { onlyIfTrusted: false };
          debugLog("Connect options:", connectOptions);
          
          await phantom.connect(connectOptions);
          
          // Log wallet state after connection
          debugLog("Connection successful! Wallet state:", {
            hasPublicKey: !!phantom.publicKey,
            publicKey: phantom.publicKey?.toString(),
            isConnected: !!phantom.isConnected
          });
          
          // Wait a bit longer after connection
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Add this right after successful connection
          if (networkEnvironment === 'devnet') {
            debugLog("Checking if wallet is actually on devnet...");
            const correctNetwork = await checkWalletNetwork(phantom, 'devnet');
            
            if (!correctNetwork) {
              const proceedAnyway = window.confirm(
                "⚠️ WARNING! Your wallet appears to be on MAINNET, but this transaction is for DEVNET.\n\n" +
                "If you continue, funds may be lost!\n\n" +
                "Please go to Phantom Settings → Developer Settings → Change Network → Devnet\n\n" +
                "Are you ABSOLUTELY SURE you want to proceed?"
              );
              
              if (!proceedAnyway) {
                throw new Error("Transaction canceled - please switch your wallet to Devnet network");
              }
            }
          }
        } catch (connError) {
          // Detailed connection error logging
          console.error("Connection failed with error:", connError);
          debugLog("Error type:", typeof connError);
          debugLog("Error name:", connError.name);
          debugLog("Error message:", connError.message);
          debugLog("Error stack:", connError.stack);
          
          // Try to extract more information if available
          if (connError.code) {
            debugLog("Error code:", connError.code);
          }
          
          if (networkEnvironment === 'devnet') {
            throw new Error("Could not connect to Phantom on devnet. Please make sure you've configured Phantom for devnet in Settings.");
          } else {
            throw new Error("Could not connect to Phantom. Please ensure your wallet is unlocked.");
          }
        }
      }
      
      // Check again for public key
      if (!phantom.publicKey) {
        console.error("Public key still not available after connection attempt");
        throw new Error("Failed to get wallet public key. Please try reconnecting your wallet.");
      }
      
      // Continue with transaction preparation
      debugLog("Moving to transaction preparation...");
      
      let connection = null;
      let endpoint = null;
      let latestBlockhash = null;

      try {
        const connectionInfo = await getSolanaConnectionWithFallback(
          networkEnvironment,
          {
            probe: (rpcConnection) => rpcConnection.getLatestBlockhash('confirmed')
          }
        );
        connection = connectionInfo.connection;
        endpoint = connectionInfo.endpoint;
        latestBlockhash = connectionInfo.result;
        debugLog(`Using ${networkEnvironment.toUpperCase()} RPC endpoint:`, endpoint);
      } catch (rpcError) {
        debugLog('Public RPC blockhash failed, falling back to backend', {
          error: rpcError?.message || String(rpcError)
        });
        latestBlockhash = await fetchBlockhashFromBackend({ sessionToken, sessionSignature });
      }
      
      const sender = new PublicKey(phantom.publicKey.toString());
      const recipient = new PublicKey(paymentAddress);
      
      debugLog('Transaction details:', {
        sender: sender.toString(),
        recipient: recipient.toString(),
        amount,
        token: tokenSymbol,
        networkEnvironment,  // log this explicitly
        network: network,    // and this for clarity
        decimals: resolvedTokenDecimals
      });
      
      // Parse amount with correct decimals
      const amountInBaseUnits = parseTokenAmountToBaseUnits(amount, resolvedTokenDecimals);
      if (amountInBaseUnits <= 0n) {
        throw new Error("Amount must be greater than zero");
      }

      debugLog('Parsed amount (base units):', {
        amount,
        decimals: tokenInfo.decimals,
        baseUnits: amountInBaseUnits.toString()
      });
      
      let transaction = new Transaction();
      transaction.feePayer = sender;

      const blockhash = latestBlockhash?.blockhash;
      const lastValidBlockHeight = latestBlockhash?.lastValidBlockHeight;
      if (!blockhash || !Number.isFinite(lastValidBlockHeight)) {
        throw new Error("Failed to fetch recent blockhash");
      }

      if (!connection && tokenSymbol !== 'SOL' && (!sessionToken || !sessionSignature)) {
        throw new Error('Solana RPC is temporarily unavailable for token transfers. Please try again.');
      }
      
      // EXPLICITLY SET THE TRANSACTION PROPERTIES
      if (networkEnvironment === 'devnet') {
        debugLog("Creating DEVNET transaction");

        transaction.recentBlockhash = blockhash;
        transaction.lastValidBlockHeight = lastValidBlockHeight;

        // Log detailed transaction info
        debugLog("Transaction configured for DEVNET with:", {
          blockhash,
          lastValidBlockHeight,
          feePayer: sender.toString()
        });
      } else {
        debugLog("Creating MAINNET transaction");
        transaction.recentBlockhash = blockhash;
        transaction.lastValidBlockHeight = lastValidBlockHeight;
      }
      
      // For native SOL
      if (tokenSymbol === 'SOL') {
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: sender,
            toPubkey: recipient,
            lamports: amountInBaseUnits,
          })
        );
      } 
      // For SPL tokens (like USDC)
      else if (resolvedMintAddress) {
        debugLog(`Processing ${tokenSymbol} token transfer`);
        
        try {
          // Get the mint address from your networkTokens utility
          const mintAddress = resolvedMintAddress;
          if (!mintAddress) {
            throw new Error(`Mint address not found for ${tokenSymbol} on ${networkEnvironment}`);
          }
          debugLog(`Using mint address for ${tokenSymbol}: ${mintAddress}`);
          
          // Convert string addresses to PublicKey objects
          const mintPublicKey = new PublicKey(mintAddress);
          const recipientPublicKey = recipient;
          const senderPublicKey = sender;
          const transferContext = await resolveSplTransferContext({
            connection,
            sessionToken,
            sessionSignature,
            mintPublicKey,
            senderPublicKey,
            recipientPublicKey
          });
          const tokenProgramId = transferContext.tokenProgramId;
          const recipientTokenAccount = transferContext.recipientTokenAccount;
          const senderTokenAccount = transferContext.senderTokenAccount;

          debugLog('Resolved token program:', {
            mintAddress,
            programId: tokenProgramId.toBase58()
          });

          if (!senderTokenAccount || !transferContext.senderTokenAccountExists) {
            throw new Error(`No ${tokenSymbol} token account found for this wallet`);
          }

          debugLog(`Recipient token account address: ${recipientTokenAccount.toString()}`);
          debugLog(`Sender token account address: ${senderTokenAccount.toString()}`);

          if (transferContext.recipientTokenAccountExists !== true) {
            debugLog(`Creating token account for recipient: ${recipient}`);
            transaction.add(
              createAssociatedTokenAccountIdempotentInstruction(
                senderPublicKey, // payer
                recipientTokenAccount, // associatedToken
                recipientPublicKey, // owner
                mintPublicKey, // mint
                tokenProgramId,
                ASSOCIATED_TOKEN_PROGRAM_ID
              )
            );
            debugLog('Added instruction to create token account');
          } else {
            debugLog('Recipient token account already exists');
          }

          const tokenAmount = amountInBaseUnits;
          if (transferContext.senderTokenBalance !== null && transferContext.senderTokenBalance !== undefined) {
            const senderTokenBalanceAmount = BigInt(transferContext.senderTokenBalance || '0');
            if (senderTokenBalanceAmount < tokenAmount) {
              throw new Error(`Insufficient ${tokenSymbol} balance`);
            }
          } else {
            debugLog('Skipping token balance check due to unavailable balance');
          }

          debugLog(`Transferring ${tokenAmount.toString()} units of ${tokenSymbol} (${amount} with ${resolvedTokenDecimals} decimals)`);
          
          // Add transfer instruction - this should happen in BOTH cases
          transaction.add(
            createTransferCheckedInstruction(
              senderTokenAccount,           // source
              mintPublicKey,                // mint
              recipientTokenAccount,        // destination
              senderPublicKey,              // owner
              tokenAmount,                  // amount to transfer (in smallest units)
              resolvedTokenDecimals,        // decimals
              [],                           // multisig signers (none in this case)
              tokenProgramId
            )
          );
          
          debugLog(`Added instruction to transfer ${amount} ${tokenSymbol} to ${recipient}`);
        } catch (error) {
          console.error('Error preparing token transfer:', error);
          throw new Error(`Error preparing ${tokenSymbol} transfer: ${error.message}`);
        }
      }
      
      if (networkEnvironment === 'devnet') {
        debugLog("FINAL CHECK: Sending transaction to DEVNET");
        
        // Add special options to signAndSendTransaction for devnet 
        const result = await phantom.signAndSendTransaction(transaction, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 5,
        });
        
        signature = result.signature; // Store the signature from the result
        debugLog('DEVNET transaction sent:', signature);
      } else {
        // Regular mainnet send
        const result = await phantom.signAndSendTransaction(transaction);
        signature = result.signature; // Store the signature from the result
        debugLog('MAINNET transaction sent:', signature);
      }
      
      // Now signature is available in this scope
      // You can provide a link to Solana Explorer
      const explorerUrl = networkEnvironment === 'devnet'
        ? `https://explorer.solana.com/tx/${signature}?cluster=devnet`
        : `https://explorer.solana.com/tx/${signature}`;
      
      debugLog('Transaction explorer link:', explorerUrl);

      if (onTransactionSubmitted) {
        onTransactionSubmitted({
          hash: signature,
          explorerUrl,
          network: 'solana',
          environment: networkEnvironment,
          tokenSymbol,
          amount
        });
      } else if (onSuccess) {
        onSuccess(`Transaction submitted! [${signature.slice(0, 8)}...]`);
      }

      if (!connection) {
        debugLog('Skipping confirmation due to unavailable RPC connection');
        return;
      }

      const confirmation = await confirmTransactionWithFallback({
        connection,
        endpoint,
        networkEnvironment,
        signature,
        blockhash: transaction.recentBlockhash,
        lastValidBlockHeight: transaction.lastValidBlockHeight
      });

      if (confirmation?.value?.err) {
        throw new Error('Transaction failed');
      }

      if (onTransactionConfirmed) {
        onTransactionConfirmed({
          hash: signature,
          explorerUrl,
          network: 'solana',
          environment: networkEnvironment,
          tokenSymbol,
          amount
        });
      } else if (onSuccess) {
        onSuccess(`Transaction confirmed! [${signature.slice(0, 8)}...]`);
      }
    } catch (error) {
      console.error("Payment process failed:", error);
      if (signature) {
        if (onTransactionFailed) {
          const explorerUrl = networkEnvironment === 'devnet'
            ? `https://explorer.solana.com/tx/${signature}?cluster=devnet`
            : `https://explorer.solana.com/tx/${signature}`;
          onTransactionFailed({
            hash: signature,
            explorerUrl,
            network: 'solana',
            environment: networkEnvironment,
            tokenSymbol,
            amount,
            errorMessage: getReadableError(error)
          });
        } else if (onError) {
          onError(getReadableError(error));
        }
      } else if (onError) {
        onError(getReadableError(error));
      }
    } finally {
      setSending(false);
    }
  };
  
  // Simplified rendering - just one button state
  if (!phantom) {
    return (
      <WalletButton 
        variant="contained"
        startIcon={<AccountBalanceWalletIcon />}
        onClick={() => window.open("https://phantom.app/", "_blank")}
      >
        Install Phantom Wallet
      </WalletButton>
    );
  }
  
  return (
    <Box>
      <WalletButton
        variant="contained"
        startIcon={<AccountBalanceWalletIcon />}
        onClick={handlePayment}
        disabled={sending || isExpiringSoon || isExpired || paymentDisabled}
        sx={{
          bgcolor: networkEnvironment === 'devnet' ? '#f57c00' : '#9945FF',
          '&:hover': { bgcolor: networkEnvironment === 'devnet' ? '#e65100' : '#8134E8' },
        }}
      >
        {sending ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
            Processing Payment...
          </Box>
        ) : isExpiringSoon ? (
          "Price quote expiring soon"
        ) : isExpired ? (
          "Price quote expired"
        ) : (
          `Pay ${displayAmount} ${tokenSymbol} with Phantom ${networkEnvironment === 'devnet' ? '(DEVNET)' : ''}`
        )}
      </WalletButton>
      
      {(isExpiringSoon || isExpired) && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          ⚠️ {isExpired ? "Price quote has expired. Please refresh to get new rate." : "Price quote expiring in less than a minute. Please refresh."}
        </Typography>
      )}
      
      {networkEnvironment === 'devnet' && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          ⚠️ This is a DEVNET transaction. Make sure Phantom is configured for devnet.
        </Typography>
      )}
    </Box>
  );
};

export default SolanaWalletButton;
